/**
 * Create Company Drawer Component
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../components/common/RightContentDrawer';

import ButtonGroup from '../../../components/common/ButtonGroup';

// ========== TYPE DEFINITIONS ==========

export interface CompanyFormData {
    companyId: string;
    companyName: string;
    annualReportId: string;
}

export interface ProcessItemDrawerProps {
    open: boolean;
    initialValues?: Partial<CompanyFormData>;
    nextProcessCode?: string; // For add mode: the next process code (A01, A02...)
    autoEnterSearchMode?: boolean; // Auto enter search mode on open (for missing owner)
    onClose: () => void;
    onSubmit: (values: CompanyFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;

    &:hover {
      color: #6B7280;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;


const CharacterCounter = styled.div<{ $isNearLimit?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.$isNearLimit ? '#EF4444' : '#9CA3AF'};
  margin-top: 4px;
  text-align: right;
`;

// ========== MAIN COMPONENT ==========

const CreateCompanyDrawer: React.FC<ProcessItemDrawerProps> = ({
    open,
    onClose,
    onSubmit,
}) => {
    // Form states
    const [form] = Form.useForm();
    const [companyName, setCompanyName] = useState('');

    // UI states
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    // Reset form when initialValues change
    useEffect(() => {
        resetForm();
        
    }, []);

    // Watch for field changes and validate
    useEffect(() => {
        const checkFormValidation = () => {
            const hasCompanyName = !!companyName && companyName.trim() !== '';

            const isValid = hasCompanyName;
            setIsFormValid(isValid);
        };

        checkFormValidation();
    }, [companyName]);

    const resetForm = () => {
        setCompanyName('');
        form.resetFields();
    };


    const handleClose = () => {
        setIsFormValid(false);
        resetForm();
        onClose();
    };

    // Handle drawer close button (X) - context-aware
    const handleDrawerClose = () => {
        
        handleClose();
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            


            // Submit form with file IDs
            await onSubmit({
                companyId: "",
                companyName: companyName,
                annualReportId: ""
            });

            handleClose();
        } catch (error) {
            console.error("Form submission failed:", error);
        } finally {
            setLoading(false);
        }
    };

      
    // Render form UI
    const renderFormUI = () => (
        <StyledForm
            id="create-form"
            form={form}
            layout="vertical"
            requiredMark={false}
        >

            {/* First Name */}
            <Form.Item
                name="companyName"
                rules={[
                    { required: true, message: 'Please enter a company name' },
                    { whitespace: true, message: 'Company name cannot be empty' },
                ]}
            >
                <div>
                    <FormLabel>New Company Name</FormLabel>
                    <StyledInput
                        value={companyName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setCompanyName(value);
                            }
                        }}
                        placeholder="Enter new company name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={companyName.length >= 90}>
                        {companyName.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>


        </StyledForm>
    );

    const drawerTitle = 'Create New Company';

    return (
        <RightContentDrawer
            open={open}
            title={drawerTitle}
            width={572}
            onClose={handleDrawerClose}
            footer={
               
                // Normal mode footer: Go back + Submit buttons
                <ButtonGroup>
                    <Button type="default" onClick={handleClose} disabled={loading}>
                        Go back
                    </Button>
                    <Button
                        type="primary"
                        loading={loading}
                        disabled={!isFormValid}
                        form="create-form"
                        onClick={handleFormSubmit}
                    >
                        Create Company
                    </Button>
                </ButtonGroup>
                
            }
        >
            {renderFormUI()}
        </RightContentDrawer>
    );
};

export default CreateCompanyDrawer;

